import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useParams, useNavigate } from 'react-router-dom';
import PrintPage from './views/print/PrintPage';
import ScheduleV6Header from './header/ScheduleV6Header';
import ResourceSidebar from './resources/ResourceSidebar';
import CalendarLayout from './layout/CalendarLayout';
import useScheduleCoreLogic from './hooks/useScheduleCoreLogic';
import useScheduleViewSync from './hooks/useScheduleViewSync';
import ResourceActionsBar from './modals/ResourceActionsBar/ResourceActionsBar';
import useHasEditAccess from './hooks/useHasEditAccess';
import { parse } from 'date-fns';

/**
 * Desktop layout for ScheduleV6.
 *
 * Responsibilities:
 * - render desktop header, resource sidebar, calendar, and print page
 * - keep URL date and Redux selected date in sync
 * - manage calendar resize on sidebar toggle
 *
 * Shared infrastructure (drawers, modals, WorklogDetails) lives in SchedulePageShell.
 */
const ScheduleV6PageLayout = ({
  selectedDate,
  scheduleDayView,
  scheduleView,
  filters,
  dateFormat,
  timeFormat,
  openDrawer,
  closeDrawer,
  showPublishModal,
  showUpdateModal,
  canPublish,
  canUpdate,
  showReconcileModal,
  noClSheets,
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const calendarRef = useRef(null);

  const { hasEditAccess } = useHasEditAccess();
  const core = useScheduleCoreLogic();
  const { changeSelectedDateFromUrl } = core;
  useScheduleViewSync();

  const [isResourcesOpen, setIsResourcesOpen] = useState(true);

  useEffect(() => {
    if (params.date) {
      changeSelectedDateFromUrl(params.date);
    }
  }, [params.date, changeSelectedDateFromUrl]);

  const updateCalendarSizesIncrementally = (i = 0) => {
    if (i >= 2) return;
    setTimeout(() => {
      calendarRef.current?.getApi?.()?.updateSize?.();
      updateCalendarSizesIncrementally(i + 1);
    }, 100);
  };

  const toggleResources = (isOpen) => {
    setIsResourcesOpen(isOpen);
    updateCalendarSizesIncrementally();
  };

  const effectiveSelectedDate = useMemo(
    () => (params.date ? parse(params.date, 'yyyy-MM-dd', new Date()) : selectedDate),
    [params.date, selectedDate]
  );

  useEffect(() => {
    if (calendarRef?.current) {
      calendarRef.current?.getApi().gotoDate(effectiveSelectedDate);
    }
  }, [effectiveSelectedDate, calendarRef]);

  return (
    <>
      <div className={classnames('schedule-v6-root-page', 'hide-for-print')}>
        <ScheduleV6Header
          selectedDate={effectiveSelectedDate}
          openDrawer={(type) => () => openDrawer(type)}
          showPublishModal={showPublishModal}
          showUpdateModal={showUpdateModal}
          canPublish={canPublish}
          canUpdate={canUpdate}
          showReconcileModal={showReconcileModal}
          updateSetWorklogsControl={core.updateSetWorklogsControl}
          hasEditAccess={hasEditAccess}
        />

        <div style={{ display: 'flex' }}>
          <ResourceSidebar isShow onChange={toggleResources} />
          <CalendarLayout
            calendarRef={calendarRef}
            selectedDate={effectiveSelectedDate}
            scheduleDayView={scheduleDayView}
            scheduleView={scheduleView}
            filters={filters}
            navigate={navigate}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            isResourcesOpen={isResourcesOpen}
            noClSheets={noClSheets}
            hasEditAccess={hasEditAccess}
          />
          <ResourceActionsBar />
        </div>
      </div>

      <PrintPage
        scheduleView={scheduleView}
        selectedDate={selectedDate}
        worklogsForPrint={core.worklogsForPrint}
        updatePrintWorklogsControl={core.updatePrintWorklogsControl}
        needToPrint={core.needToPrint}
      />
    </>
  );
};

ScheduleV6PageLayout.propTypes = {
  selectedDate: PropTypes.instanceOf(Date),
  scheduleDayView: PropTypes.string.isRequired,
  scheduleView: PropTypes.string.isRequired,
  filters: PropTypes.object,
  dateFormat: PropTypes.string,
  timeFormat: PropTypes.string,
  openDrawer: PropTypes.func.isRequired,
  closeDrawer: PropTypes.func.isRequired,
  showPublishModal: PropTypes.func.isRequired,
  showUpdateModal: PropTypes.func.isRequired,
  canPublish: PropTypes.bool,
  canUpdate: PropTypes.bool,
  showReconcileModal: PropTypes.func.isRequired,
  noClSheets: PropTypes.array,
};

export default ScheduleV6PageLayout;
